// src/propertyManagement/pages/PMReviewPayment.jsx
import React from 'react';
import { Box, Typography, Paper, Divider, Button, Grid } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, updateDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoPng from '../../assets/901homesolutions.png';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

export default function PMReviewPayment() {
  const { state } = useLocation();
  const navigate = useNavigate();
  if (!state) return <Typography>Missing payment info</Typography>;

  const {
    vendorName,
    vendorId,
    jobId,
    paymentAmount,
    fee,
    formattedDate,
    paymentMethod
  } = state;

const serviceFee = paymentMethod === 'instant'
  ? +(paymentAmount * 0.03).toFixed(2)
  : fee;
    const netAmount = paymentAmount - serviceFee;
  const methodLabel = paymentMethod === 'instant' ? 'Instant deposit' : 'EFT';

  const handleSubmit = async () => {
    try {
      // 1) Close work order
      const woRef = doc(db, 'workOrders', jobId);
      await updateDoc(woRef, { status: 'Closed' });

      // 2) Add system update
      await addDoc(collection(db, 'workOrders', jobId, 'updates'), {
        message: `Payment of $${paymentAmount.toFixed(2)} recorded; closing work order.`,
        sender: 'System',
        system: true,
        createdAt: serverTimestamp()
      });

      // 3) Record vendor payment
      await addDoc(collection(db, 'vendors', String(vendorId), 'payments'), {
        jobId,
        amount: paymentAmount,
        fee: serviceFee,
        netAmount,
        date: formattedDate,
        paymentMethod,
        createdAt: serverTimestamp()
      });

      // 4) Generate & upload receipt PDF
      await generateReceiptPdf({
        jobId,
        vendorName,
        formattedDate,
        methodLabel,
        paymentAmount,
        serviceFee,
        netAmount
      });

      // 5) Navigate on success
      navigate('/pm/maintenance/paymentProcessing', { state });
    } catch (err) {
      console.error('Error recording payment:', err);
    }
  };

  return (
    <Box maxWidth="700px" mx="auto" my={5}>
      <Typography variant="h6" mb={2}>Review payment</Typography>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Typography fontSize={28} fontWeight={700} mb={2}>
          Amount to contractor
        </Typography>
        <Typography fontSize={28} fontWeight={700} mb={2}>
          ${netAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Box mt={2}>
          <Grid container>
            <Grid item xs={6}>
              <Typography>{vendorName}</Typography>
            </Grid>
            <Grid item xs={6} textAlign="right">
              <Typography>${paymentAmount.toFixed(2)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>{methodLabel} service fee</Typography>
            </Grid>
            <Grid item xs={6} textAlign="right">
              <Typography>${serviceFee.toFixed(2)}</Typography>
            </Grid>
          </Grid>
        </Box>
        <Divider sx={{ my: 2 }} />

        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography fontWeight={600}>Net total</Typography>
          <Typography fontWeight={600}>${netAmount.toFixed(2)}</Typography>
        </Box>

        <Grid container spacing={2} mt={2}>
          <Grid item xs={4}>
            <Typography fontSize={13} color="text.secondary">Payment date</Typography>
            <Typography fontWeight={600}>{formattedDate}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography fontSize={13} color="text.secondary">Paid to</Typography>
            <Typography fontWeight={600}>{vendorName}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography fontSize={13} color="text.secondary">Payment method</Typography>
            <Typography fontWeight={600}>{methodLabel}</Typography>
          </Grid>
        </Grid>

        <Typography fontSize={12} color="text.secondary" mt={3}>
          By clicking <strong>Submit payment</strong>, I authorize the above amount to be paid to the contractor.
        </Typography>

        <Box display="flex" gap={2} mt={4}>
          <Button fullWidth variant="outlined" onClick={() => navigate('/pm/maintenance/vendors')}>
            Cancel
          </Button>
          <Button fullWidth variant="contained" color="success" onClick={handleSubmit}>
            Submit payment
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

// ----------------------------------------------------------------------------
// Receipt PDF generator
// ----------------------------------------------------------------------------
async function generateReceiptPdf({
  jobId,
  vendorName,
  formattedDate,
  methodLabel,
  paymentAmount,
  serviceFee,
  netAmount
}) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 40;

  // Logo + Title
  const img = new Image();
  img.src = logoPng;
  await new Promise((r) => (img.onload = r));
  const logoW = 100;
  const logoH = (img.height / img.width) * logoW;
  doc.addImage(img, 'PNG', margin, margin, logoW, logoH);

  doc.setFontSize(18);
  doc.text('Payment Receipt', margin + logoW + 20, margin + logoH / 2);

  // Details
  doc.setFontSize(10);
  const detailY = margin + logoH + 30;
  doc.text(`Date: ${formattedDate}`, margin, detailY);
  doc.text(`Receipt #: ${jobId}`, pageW - margin, detailY, { align: 'right' });
  doc.text(`Paid To: ${vendorName}`, margin, detailY + 15);
  doc.text(`Method: ${methodLabel}`, margin, detailY + 30);

  // Line-item table
  autoTable(doc, {
    startY: detailY + 60,
    margin: { left: margin, right: margin },
    head: [['Description', 'Amount']],
    body: [
      ['Gross Amount', `$${paymentAmount.toFixed(2)}`],
      ['Service Fee', `$${serviceFee.toFixed(2)}`],
      ['Net Amount', `$${netAmount.toFixed(2)}`]
    ],
    styles: { fontSize: 10 },
    headStyles: { fillColor: [0, 102, 153], textColor: 255 }
  });

  // Thank you footer
  const footerY = doc.lastAutoTable.finalY + 30;
  doc.setFontSize(12);
  doc.text('Thank you for your payment!', margin, footerY);

  // Upload PDF blob to Storage & record in Firestore
  const blob = doc.output('blob');
  const storage = getStorage();
  const receiptRef = storageRef(
    storage,
    `workOrders/${jobId}/receipts/receipt-${Date.now()}.pdf`
  );
  await uploadBytes(receiptRef, blob);
  const receiptUrl = await getDownloadURL(receiptRef);

  await addDoc(collection(db, 'workOrders', jobId, 'files'), {
    name: `receipt-${jobId}.pdf`,
    url: receiptUrl,
    type: 'receipt',
    createdAt: serverTimestamp()
  });
}